// 📱 설정 › 원장 호출 — 「받는 휴대폰」 (2026-10-13 대표 Q8 "권고대로": 분실·퇴사 대비)
// 서버 lib/doctor-call-push.js listReceiversFor·removeReceiversFor — 대표만(직원이 원장 휴대폰 수신을 끄면 호출이 조용히 끊긴다)
// 화면에는 누구 계정·기기 종류·켠 날짜만. 토큰·기기 id 는 서버가 보내지 않는다
import React, { useCallback, useEffect, useState } from 'react';
import { listDoctorCallReceivers, removeDoctorCallReceivers } from '../../services/doctorCallApi';
import { doctorCallError } from '../../utils/doctorCall';

const PLATFORM_TEXT = Object.freeze({ android: '휴대폰(안드로이드)', ios: '휴대폰(아이폰)', desktop: '원장실 PC', unknown: '알 수 없는 기기' });
const dateText = (ms) => {
    if (!Number.isFinite(ms)) return '-';
    const k = new Date(ms + 9 * 3600000);
    return `${k.getUTCFullYear()}.${String(k.getUTCMonth() + 1).padStart(2, '0')}.${String(k.getUTCDate()).padStart(2, '0')}`;
};

function DoctorCallReceivers({ isOwner }) {
    const [state, setState] = useState({ loading: true, list: [], max: 10, err: '' });
    const [busy, setBusy] = useState('');       // '' | key | 'all'
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        setState(s => ({ ...s, loading: true, err: '' }));
        try {
            const d = await listDoctorCallReceivers();
            setState({ loading: false, list: Array.isArray(d.receivers) ? d.receivers : [], max: d.max || 10, err: '' });
        } catch (e) {
            console.error('[원장 호출 설정] 받는 기기 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');
            const code = String(e?.code || '');
            const notReady = code.includes('not-found') && !e?.details?.code;
            setState({ loading: false, list: [], max: 10, err: notReady ? '서버 준비 중입니다. 잠시 후 다시 열어 주세요.' : doctorCallError(e, '받는 기기 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        }
    }, []);
    useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

    const remove = async (arg, label) => {
        if (busy) return;
        const ask = arg.all ? `호출 받는 휴대폰 ${state.list.length}대를 모두 끕니다. 다시 받으려면 각 휴대폰에서 직접 켜야 합니다. 끌까요?` : `${label}의 받기를 끕니다. 끌까요?`;
        if (!window.confirm(ask)) return;
        setBusy(arg.all ? 'all' : arg.key); setNotice(null);
        try {
            const r = await removeDoctorCallReceivers(arg);
            setNotice({ type: 'ok', text: `${r.removed || 0}대를 껐습니다. 그 기기로는 이제 호출 알림이 가지 않습니다.` });
        } catch (e) {
            console.error('[원장 호출 설정] 받는 기기 끄기 실패:', e?.code || e?.message || e, e?.details?.code || '');
            setNotice({ type: 'err', text: e?.details?.code === 'receiver-gone' ? '이미 꺼진 기기입니다.' : doctorCallError(e, '끄지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        } finally {
            setBusy('');
            load();
        }
    };

    return (
        <div style={S.box}>
            <div style={{ ...S.title, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{'호출 받는 휴대폰'}</span>
                {isOwner && !state.loading && !state.err && <span style={S.sub}>{`${state.list.length}대 / 최대 ${state.max}대`}</span>}
                {isOwner && state.list.length > 1 && (
                    <button type="button" disabled={!!busy} onClick={() => remove({ all: true })} style={{ ...S.btnWarn, marginLeft: 'auto' }}>{busy === 'all' ? '끄는 중…' : '모두 끄기'}</button>
                )}
            </div>
            {!isOwner ? <div style={S.sub}>{'받는 기기 관리는 치과 대표만 할 수 있습니다. 내 휴대폰은 덴클 앱 › 원장 호출에서 켜고 끌 수 있습니다.'}</div>
                : state.loading ? <div style={S.sub}>{'불러오는 중…'}</div>
                : state.err ? <div style={S.errText}>{state.err}</div>
                : state.list.length === 0 ? <div style={S.sub}>{'호출을 받는 휴대폰이 없습니다. 덴클 앱 › 원장 호출에서 「이 휴대폰으로 받기」를 켜면 여기에 나옵니다.'}</div>
                : (
                    <div>
                        {state.list.map(r => (
                            <div key={r.key} style={S.row}>
                                <span style={{ fontWeight: 700, minWidth: 110 }}>{r.who}</span>
                                <span style={{ color: '#475569' }}>{PLATFORM_TEXT[r.platform] || PLATFORM_TEXT.unknown}</span>
                                <span style={S.sub}>{`켠 날 ${dateText(r.updatedAtMs)}`}</span>
                                {r.role === 'former' && <span style={S.badge}>{'퇴사·소속 해제'}</span>}
                                <button type="button" disabled={!!busy} onClick={() => remove({ key: r.key }, `${r.who} · ${PLATFORM_TEXT[r.platform] || ''}`)} style={{ ...S.btnSm, marginLeft: 'auto' }}>{busy === r.key ? '끄는 중…' : '끄기'}</button>
                            </div>
                        ))}
                    </div>
                )}
            {notice && <div style={notice.type === 'ok' ? S.okText : S.errText}>{notice.text}</div>}
            <div style={S.help}>{'휴대폰을 잃어버렸거나 직원이 그만두면 여기서 끄세요. 알림은 휴대폰 상태에 따라 늦을 수 있습니다. 응답이 없으면 진료실·데스크 화면에서 계속 알립니다. 원장실 PC 프로그램은 치과 계정 로그인으로 받으므로 여기에 나오지 않습니다 — 그 PC에서 로그아웃하거나 계정 비밀번호를 바꾸면 멈춥니다.'}</div>
        </div>
    );
}

const S = {
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    title: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10, flexWrap: 'wrap' },
    row: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', fontSize: 13.5 },
    sub: { fontSize: 12, color: '#94a3b8' },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    badge: { fontSize: 11.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '1px 7px' },
    btnSm: { border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', minHeight: 32 },
    btnWarn: { border: '1px solid #f5cfcf', background: '#fdecec', color: '#8a2020', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 32 },
    okText: { fontSize: 12.5, color: '#065f46', marginTop: 8 },
    errText: { fontSize: 12.5, color: '#8a2020', marginTop: 8 },
};

export default DoctorCallReceivers;
